import { OverlayModule } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import type { CustomerOption, OrderFilters } from '@asset-solutions/customer-portal/orders/models';
import { debounceTime, fromEvent, map } from 'rxjs';

import { OrderSearchStore } from '../order-search.store';

@Component({
  selector: 'orders-filter-form',
  imports: [
    FormsModule,
    MatSelectModule,
    MatInputModule,
    MatIcon,
    MatButton,
    MatIconButton,
    MatDatepickerModule,
    OverlayModule
  ],
  templateUrl: './filter-form.component.html',
  styleUrls: ['./filter-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterFormComponent implements AfterViewInit, OnChanges {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Output() filtersChange = new EventEmitter<OrderFilters>();
  @Output() resetFilters = new EventEmitter<void>();
  @Input({ required: true }) customerOptions!: CustomerOption[];
  @Input({ required: true }) filters!: OrderFilters;
  @Input({ required: true }) fleetOptions!: string[];

  private searchStore = inject(OrderSearchStore);
  protected hiddenFilterCount = 0;
  protected overlayOpen = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      this.hiddenFilterCount = (
        ['arrivalStatus', 'isAssigned', 'tempRange', 'fleetIds', 'customerIds'] as (keyof OrderFilters)[]
      ).reduce((count, key) => {
        count += this.filters[key]?.length || 0;
        return count;
      }, 0);
    }
  }

  public ngAfterViewInit(): void {
    this.searchStore.updateSearchTerm(
      fromEvent(this.searchInput.nativeElement, 'input').pipe(
        debounceTime(200),
        map(() => this.searchInput.nativeElement.value)
      )
    );
  }

  public clearSearch(): void {
    this.searchInput.nativeElement.value = '';
    this.searchStore.updateSearchTerm('');
  }

  public clearPickupDates(): void {
    this.filters.pickupStartDate = null;
    this.filters.pickupEndDate = null;
    this.emitChange();
  }

  public emitChange(): void {
    if (this.overlayOpen) {
      this.overlayOpen = false;
    }
    this.filtersChange.emit(this.filters);
  }

  public onDateChange(startOrEnd: 'start' | 'end', { value }: MatDatepickerInputEvent<Date>): void {
    // We need to catch the date changes instead of using 2-way binding because ngrx is set up to require both
    // action & state serializability, and date objects are not serializable. This way we convert them to strings.
    if (startOrEnd === 'start') {
      this.filters.pickupStartDate = value?.toISOString() || null;
    } else {
      value?.setUTCHours(23, 59, 59);
      this.filters.pickupEndDate = value?.toISOString() || null;
    }
  }

  public resetForm(): void {
    this.overlayOpen = false;
    this.resetFilters.emit();
    this.searchInput.nativeElement.value = '';
    this.searchInput.nativeElement.dispatchEvent(new InputEvent('input'));
    this.searchInput.nativeElement.focus();
  }

  public toggleOverlay(): void {
    this.overlayOpen = !this.overlayOpen;
  }
}
